import { useState, useEffect, useCallback } from 'react'

import { getPostalCodeConfiguration } from 'data/api/get-postal-code-configuration'
import { transformPostalCodeDto } from 'data/transformers/postal-code'
import { CountryModel } from 'types/models/country'

import { InputDropdownOption } from './Dsa/components/InputDropdownController'

function useCityOptions(country: CountryModel | undefined, postalCode: string) {
  const [cityOptions, setCityOptions] = useState<Array<InputDropdownOption>>([])
  const [fullPostalCode, setFullPostalCode] = useState<string>()

  const isPostalCodeValid = useCallback(
    (code: string) => {
      if (!country?.postalCodeConstraints) return false

      const { format, minLength, maxLength } = country.postalCodeConstraints
      const regExp = new RegExp(format)

      return code.length >= minLength && code.length <= maxLength && regExp.test(code)
    },
    [country],
  )

  useEffect(() => {
    if (!country || !postalCode) return

    if (!isPostalCodeValid(postalCode)) {
      setCityOptions([])

      return
    }

    async function fetchPostalCodeConfiguration() {
      if (!country) return

      const response = await getPostalCodeConfiguration({
        code: postalCode,
        countryId: country.id,
      })

      if ('errors' in response) {
        setCityOptions([])
        setFullPostalCode('')

        return
      }

      const { cities, code } = transformPostalCodeDto(response.postal_code)

      setCityOptions(cities.map(city => ({ value: city, label: city })))
      setFullPostalCode(code)
    }

    fetchPostalCodeConfiguration()
  }, [country, postalCode, isPostalCodeValid])

  return { cityOptions, fullPostalCode }
}

export default useCityOptions
